import { FC, useCallback } from "react";
import { useSelector } from "react-redux";
import { EmptyMessage } from "src/components";
import { PdTableCellText } from "src/components/layout/table";
import { useFocusableQuery } from "src/hooks/api";
import { tColumn } from "src/hooks/table";
import api from "src/util/apiNew";
import { formatLongAddress, formatMoney } from "src/util/gFormatting";
import { tTotalSalesReport } from "tmt_api/src/types/dtos";

import { PdSpinner, ReportTable } from "./components";
import { formatDate, getParams } from "./utils";

const getTotalSalesReport = (start: Date, end: Date) =>
  api.get<tTotalSalesReport[]>("reports/total-sales", getParams(start, end)).then((res) => res.data);

const columns: tColumn<tTotalSalesReport>[] = [
  {
    id: "retailerAccountManagerName",
    header: "Account Manager",
    accessor: (originalRow) => originalRow.retailerAccountManagerName || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "orderNumber",
    header: "Order #",
    accessor: (originalRow) => originalRow.orderNumber,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 120 },
  },
  {
    id: "orderDateInSeconds",
    header: "Order Date",
    accessor: (originalRow) => formatDate(originalRow.orderDateInSeconds),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 120 },
  },
  {
    id: "dateAcceptedInSeconds",
    header: "Approved Date",
    accessor: (originalRow) =>
      originalRow.dateAcceptedInSeconds ? formatDate(originalRow.dateAcceptedInSeconds) : "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 120 },
  },
  {
    id: "brandName",
    header: "Brand Name",
    accessor: (originalRow) => originalRow.brandName,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "retailerName",
    header: "Business Name",
    accessor: (originalRow) => originalRow.retailerName,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "connectionSource",
    header: "Source",
    accessor: (originalRow) => originalRow.connectionSource,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 150 },
  },
  {
    id: "orderStatus",
    header: "Order Status",
    accessor: (originalRow) => originalRow.orderStatus,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 180 },
  },
  {
    id: "orderTotalAmount",
    header: "Order Total",
    accessor: (originalRow) => formatMoney(originalRow.orderTotalAmount),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 120 },
  },
  {
    id: "orderShippingAmount",
    header: "Shipping Total",
    accessor: (originalRow) => formatMoney(originalRow.orderShippingAmount),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 120 },
  },
  {
    id: "salesRepName",
    header: "Sales Rep Name (if applicable)",
    accessor: (originalRow) => originalRow.salesRepName || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 250 },
  },
  {
    id: "salesRepCommissionAmount",
    header: "Sales Rep Commission",
    accessor: (originalRow) =>
      originalRow.salesRepCommissionAmount ? formatMoney(originalRow.salesRepCommissionAmount) : "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "marketplaceCommissionAmount",
    header: "Main Tab Commission",
    accessor: (originalRow) =>
      originalRow.marketplaceCommissionAmount ? formatMoney(originalRow.marketplaceCommissionAmount) : "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "marketplaceServiceAmount",
    header: "TMT Transaction fee",
    accessor: (originalRow) =>
      originalRow.marketplaceServiceAmount ? formatMoney(originalRow.marketplaceServiceAmount) : "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "shippingAddress.address1",
    header: "Shipping to Address",
    accessor: (originalRow) => formatLongAddress(originalRow.shippingAddress),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 400 },
  },
  {
    id: "purchaseOrderNumber",
    header: "PO # (if applicable)",
    accessor: (originalRow) => originalRow.purchaseOrderNumber || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "promotionCode",
    header: "Promotion code (if applicable)",
    accessor: (originalRow) => originalRow.promotionCode || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 300 },
  },
  {
    id: "promotionAmount",
    header: "Promotion discount $ value (if applicable)",
    accessor: (originalRow) => (originalRow.promotionAmount ? formatMoney(originalRow.promotionAmount) : "-"),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 400 },
  },
];

const TotalSalesTable: FC<{ period: Date[] }> = ({ period }) => {
  const config = useSelector((state) => state.TableConfigReducer.totalSales);
  const { isLoading, data } = useFocusableQuery<tTotalSalesReport[]>(
    useCallback(() => getTotalSalesReport(period[0], period[1]), [period]),
    { enabled: !!(period[0] && period[1]) },
  );

  if (isLoading) {
    return <PdSpinner />;
  }

  if (!data || data?.length === 0) {
    return <EmptyMessage message="There is no data to display" />;
  }

  return (
    <ReportTable
      data={data}
      columns={columns}
      keyExtractor={(item) => item.orderNumber}
      sortedBy="orderDateInSeconds"
      columnsConfig={config}
      tableConfigReduxKey="totalSales"
    />
  );
};

export default TotalSalesTable;
